"use client";

import { ChevronLeft, ChevronRight } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";

import { ReviewCard } from "@/components/reviews/review-card";
import type { GoogleReview } from "@/lib/google-reviews";
import { useRovingFocus } from "@/lib/use-roving-focus";

// Matches ReviewCard's truncation rule (default `tall = false`).
const TRUNCATE_AT = 180;

export function ReviewsScroller({
  reviews,
  title,
}: {
  reviews: GoogleReview[];
  title?: string;
}) {

  const focusableIndices = useMemo(
    () =>
      reviews
        .map((r, i) => (r.text.length > TRUNCATE_AT ? i : -1))
        .filter((i) => i !== -1),
    [reviews],
  );

  const firstFocusable = focusableIndices[0] ?? -1;
  const [isScrollable, setIsScrollable] = useState(false);
  const [atStart, setAtStart] = useState(true);
  const [atEnd, setAtEnd] = useState(false);
  const scrollerRef = useRef<HTMLDivElement>(null);
  const cardRefs = useRef<(HTMLLIElement | null)[]>([]);
  const buttonRefs = useRef<(HTMLButtonElement | null)[]>([]);

  // Track scroll position so the edge fades only show where there's more.
  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const update = () => {
      const maxScroll = el.scrollWidth - el.clientWidth;
      setIsScrollable(maxScroll > 4);
      setAtStart(el.scrollLeft <= 1);
      setAtEnd(el.scrollLeft >= maxScroll - 1);
    };
    update();
    el.addEventListener("scroll", update, { passive: true });
    window.addEventListener("resize", update);
    return () => {
      el.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
    };
  }, [reviews.length]);

  // Centre the focused card so its neighbours stay partly in view, making it
  // obvious the carousel holds more cards.
  const revealCard = (index: number) => {
    const scroller = scrollerRef.current;
    const card = cardRefs.current[index];
    if (!scroller || !card) return;
    const sRect = scroller.getBoundingClientRect();
    const cRect = card.getBoundingClientRect();
    const cardLeft = cRect.left - sRect.left + scroller.scrollLeft;
    const target = cardLeft + cRect.width / 2 - scroller.clientWidth / 2;
    const max = Math.max(0, scroller.scrollWidth - scroller.clientWidth);
    scroller.scrollTo({
      left: Math.max(0, Math.min(target, max)),
      behavior: "smooth",
    });
  };


  const scrollByPage = (dir: number) => {
    const el = scrollerRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * el.clientWidth * 0.85, behavior: "smooth" });
  };

  const handleKey = (
    e: React.KeyboardEvent<HTMLButtonElement>,
    index: number,
  ) => {
    if (focusableIndices.length === 0) return;
    const pos = focusableIndices.indexOf(index);
    if (pos === -1) return;
    let nextPos = pos;
    if (e.key === "ArrowRight") nextPos = (pos + 1) % focusableIndices.length;
    else if (e.key === "ArrowLeft")
      nextPos = (pos - 1 + focusableIndices.length) % focusableIndices.length;
    else if (e.key === "Home") nextPos = 0;
    else if (e.key === "End") nextPos = focusableIndices.length - 1;
    else return;
    e.preventDefault();
    const nextIndex = focusableIndices[nextPos];

    buttonRefs.current[nextIndex]?.focus({ preventScroll: true });
    revealCard(nextIndex);
  };


  const { ref: arrowsRef, rovingProps: arrowsRovingProps } =
    useRovingFocus<HTMLDivElement>();

  const arrowClass =
    "flex size-11 items-center justify-center rounded-full border border-black/10 text-purple-05 transition-colors hover:border-purple-03 hover:text-purple-03 aria-disabled:pointer-events-none aria-disabled:opacity-30";

  return (
    <>

      <div className="mx-auto flex w-full max-w-[1280px] items-center gap-6 px-[clamp(1.25rem,4vw,2.5rem)]">
        {title && (
          <h2
            className={`text-purple-05 flex-1 text-center text-[clamp(2.25rem,4vw,3rem)] leading-[1.08] font-bold ${
              isScrollable ? "lg:text-left" : ""
            }`}
          >
            {title}
          </h2>
        )}
        {/* Mouse navigation — desktop only; the carousel is swipeable on touch. */}
        {isScrollable && (
          <div
            ref={arrowsRef}
            {...arrowsRovingProps}
            className="hidden shrink-0 items-center gap-2 lg:flex"
          >
            <button
              type="button"
              data-roving
              onClick={() => scrollByPage(-1)}
              aria-disabled={atStart || undefined}
              aria-label="Previous reviews"
              className={arrowClass}
            >
              <ChevronLeft className="size-5" strokeWidth={2} />
            </button>
            <button
              type="button"
              data-roving
              onClick={() => scrollByPage(1)}
              aria-disabled={atEnd || undefined}
              aria-label="Next reviews"
              className={arrowClass}
            >
              <ChevronRight className="size-5" strokeWidth={2} />
            </button>
          </div>
        )}
      </div>
      <div className="relative mt-8 lg:mx-auto lg:mt-10 lg:max-w-[1280px] lg:px-[clamp(1.25rem,4vw,2.5rem)]">
        <div
          ref={scrollerRef}
          role="group"
          aria-label="Patient reviews"
        tabIndex={0}
        className="scrollbar-none overflow-x-auto overflow-y-clip py-1 [-ms-overflow-style:none] focus:outline-none focus-visible:rounded-[12px] focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-purple-03 [&::-webkit-scrollbar]:hidden"
      >
        <ul className="flex w-max gap-5 pr-5 pl-[clamp(1.25rem,4vw,2.5rem)] lg:px-0">
          {reviews.map((review, i) => (
            <li
              key={i}
              ref={(el) => {
                cardRefs.current[i] = el;
              }}
              className="w-[285px] shrink-0"
            >
              <ReviewCard
                review={review}
                buttonRef={(el) => {
                  buttonRefs.current[i] = el;
                }}
                tabIndex={i === firstFocusable ? 0 : -1}
                onKeyDown={(e) => handleKey(e, i)}
              />
            </li>
          ))}
        </ul>
        </div>
      </div>
    </>
  );
}
